import { FC, Dispatch, SetStateAction, useEffect } from 'react'
import cn from 'classnames'
import { useTranslation } from 'next-i18next'
import { useForm, SubmitHandler } from 'react-hook-form'
import { Wishlist, MutationUpdateWishlistArgs } from '@pylot-data/pylotschema'
import { WishlistInputVariable } from '../Hooks/use-create-wishlist'
import { WishlistVisibilityEnum } from '@pylot-data/enums/WishlistVisibilityEnum.d'
import { Button, ConfirmBox } from '@corratech/pylot-ui'
import s from '@pagestyles/Wishlist.module.scss'

export type Props = {
    show: boolean
    setShow: Dispatch<SetStateAction<boolean>>
    onSubmit: SubmitHandler<WishlistInputVariable & MutationUpdateWishlistArgs>
    wishlist?: Wishlist
    isLoading?: boolean
}

export const WishlistForm: FC<Props> = ({
    show,
    setShow,
    wishlist,
    onSubmit,
    isLoading = false
}) => {
    const { t } = useTranslation(['wishlist'])
    const {
        reset,
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<WishlistInputVariable>()

    useEffect(() => {
        if (!show) reset()
    }, [show])

    const closeConfirm = () => {
        setShow(false)
    }

    return (
        <div className={s['wishlist-create']}>
            <ConfirmBox footer={false} show={show} closeConfirm={closeConfirm}>
                <>
                    <h3>
                        {t('wishlist|{{ action }} Wish List', {
                            action: wishlist ? 'Update' : 'Create New'
                        })}
                    </h3>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group">
                            <label className="heading" htmlFor="name">
                                {t('wishlist|Wish List Name')}
                                <span className="asterisk">*</span>
                            </label>
                            <input
                                type="text"
                                defaultValue={wishlist?.name! || ''}
                                {...register('name', { required: true })}
                                className={cn('form-input', {
                                    'border-alert-danger': errors?.name
                                })}
                                id="name"
                            />
                            {errors?.name && (
                                <span className="error">
                                    {t('wishlist|This field is required')}
                                </span>
                            )}
                        </div>
                        <div className="form-group">
                            <div className="checkbox-container">
                                <input
                                    type="checkbox"
                                    className="form-checkbox block"
                                    defaultChecked={
                                        wishlist?.visibility ===
                                        WishlistVisibilityEnum.Public
                                    }
                                    {...register('visibility')}
                                    id="create-wishlist"
                                />
                                <label
                                    className="label block"
                                    htmlFor="create-wishlist"
                                >
                                    {t('wishlist|Public Wish List')}
                                </label>
                            </div>
                        </div>
                        <div className={s['btn-container']}>
                            <Button
                                disabled={isLoading}
                                loading={isLoading}
                                type="submit"
                                variant="secondary"
                                tabIndex={0}
                                className="delete"
                            >
                                {t('wishlist|{{ action }}', {
                                    action: wishlist ? 'update' : 'save'
                                })}
                            </Button>
                            <Button
                                variant="primary"
                                tabIndex={0}
                                onClick={closeConfirm}
                                className="confirm"
                            >
                                {t('wishlist|Cancel')}
                            </Button>
                        </div>
                    </form>
                </>
            </ConfirmBox>
        </div>
    )
}
